import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowUpRight, Music } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import HomeEventCarousel from '../components/HomeEventCarousel';
import MarqueeStrip from '../components/home/MarqueeStrip';
import KineticBanner from '../components/home/KineticBanner';
import OpenCallStrip from '../components/home/OpenCallStrip';
import BentoUpcomingEvents from '../components/home/BentoUpcomingEvents';
import TopOfTheWeekTeaser from '../components/home/TopOfTheWeekTeaser';
import FeaturedProducersGrid from '../components/home/FeaturedProducersGrid';
import DJsOrbit from '../components/home/DJsOrbit';
import AgencyOrbitBanner from '../components/home/AgencyOrbitBanner';
import ProducerValueProp from '../components/home/ProducerValueProp';
import MegaBravoBanner from '../components/home/MegaBravoBanner';
import BravxLeaderboardTeaser from '../components/home/BravxLeaderboardTeaser';

const SectionLabel = ({ kicker, title, link, linkLabel }) => (
  <div className="flex items-end justify-between flex-wrap gap-4 mb-8 sm:mb-12">
    <div>
      <p
        className="text-[10px] sm:text-xs uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-3"
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        {kicker}
      </p>
      <h2
        className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[0.95] tracking-tighter uppercase max-w-2xl"
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        {title}
      </h2>
    </div>
    {link && (
      <Link
        to={link}
        className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-semibold transition-colors group"
        data-testid={`section-link-${kicker.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {linkLabel || 'Ver todo'}
        <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </Link>
    )}
  </div>
);

export default function HomePage() {
  return (
    <div
      className="min-h-screen bg-[#050505] text-white overflow-x-hidden"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <SEO
        title={null}
        description="Bravo Platform — Creamos el Momento Euge. Productoras, DJs y comunidad en Argentina."
        url="/"
      />
      <Navbar />

      {/* HERO: Admin-curated featured events */}
      <section className="relative" data-testid="hero-section">
        <HomeEventCarousel />
      </section>

      {/* KINETIC BANNER — kinetic typography (replaces the old scrolling marquee) */}
      <KineticBanner
        phrases={[
          'EVENTOS ÚNICOS, GRANDES EXPERIENCIAS',
          'MOMENTO E.U.G.E.',
          'BUENOS AIRES NIGHTLIFE',
          'PRODUCTORAS · DJs · COMUNIDAD',
        ]}
        variant="gold"
      />

      {/* OPEN CALL DJs — announcement strip (renders only when there are active calls) */}
      <OpenCallStrip />

      {/* UPCOMING EVENTS — Bento grid */}
      <section
        className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8"
        data-testid="upcoming-events-section"
      >
        <div className="max-w-7xl mx-auto">
          <SectionLabel
            kicker="Lo que se viene"
            title="Próximos eventos"
            link="/eventos"
            linkLabel="Ver toda la cartelera"
          />
          <BentoUpcomingEvents />
        </div>
      </section>

      {/* TOP OF THE WEEK */}
      <section
        className="relative pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8"
        data-testid="top-of-week-section"
      >
        <div className="max-w-7xl mx-auto">
          <TopOfTheWeekTeaser />
        </div>
      </section>

      {/* BRAVX LEADERBOARD TEASER */}
      <section
        className="relative pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8"
        data-testid="home-bravx-section"
      >
        <div className="max-w-7xl mx-auto">
          <BravxLeaderboardTeaser />
        </div>
      </section>

      {/* DJs ORBIT */}
      <section
        className="relative pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8"
        data-testid="djs-section"
      >
        <div className="max-w-7xl mx-auto">
          <SectionLabel
            kicker="DJs en órbita"
            title="Quiénes están sonando"
            link="/djs"
            linkLabel="Explorar artistas"
          />
          <DJsOrbit />
        </div>
      </section>

      {/* AGENCIES DIRECTORY TEASER — small strip pointing to /agencias */}
      <AgencyOrbitBanner />

      {/* Secondary kinetic banner — dark variant */}
      <KineticBanner
        phrases={[
          'PRODUCED IN ARGENTINA',
          'MOMENTO E.U.G.E.',
          'POWERED BY THE SCENE',
          'EST. 2020',
        ]}
        variant="dark"
        interval={3600}
      />

      {/* FEATURED PRODUCERS */}
      <section
        className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8"
        data-testid="producers-section"
      >
        <div className="max-w-7xl mx-auto">
          <SectionLabel
            kicker="Productoras destacadas"
            title="Quiénes están moviendo la escena"
            link="/comunidad"
            linkLabel="Ver toda la comunidad"
          />
          <FeaturedProducersGrid />
        </div>
      </section>

      {/* VALUE PROP FOR PRODUCERS */}
      <section
        className="relative pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8"
        data-testid="value-prop-section"
      >
        <div className="max-w-7xl mx-auto">
          <ProducerValueProp />
        </div>
      </section>

      {/* MANIFESTO blurb */}
      <section
        className="relative pb-20 sm:pb-28 px-4 sm:px-6 lg:px-8"
        data-testid="manifesto-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <Sparkles size={12} className="text-[#D4AF37]" />
              <span
                className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70"
                style={{ fontFamily: "'Unbounded', sans-serif" }}
              >
                Manifiesto
              </span>
            </div>
            <p
              className="text-2xl sm:text-3xl lg:text-4xl text-white/85 leading-tight tracking-tight font-bold"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              No vendemos entradas. Creamos el
              <span className="text-[#D4AF37]"> Momento Euge</span>: noches que se cuentan al otro día.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 text-white/40 text-sm">
              <Music size={14} className="text-[#D4AF37]" />
              <span style={{ fontFamily: "'Manrope', sans-serif" }}>
                Made with care in Buenos Aires
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MEGA BRAVO closing banner */}
      <MegaBravoBanner />

      <Footer />
    </div>
  );
}
